import {
  type IoConfig,
  type NavigationContainerVariantType,
  type PropsOf,
} from '@blaze-runtime/engine-domain';
import { navigationContainerElementTypeToPageType } from '@blaze-runtime/engine-domain';
import {
  READ_EXCLUSIVE_SLOT_PARENT_PATH_PREFIX,
  READ_SLOT_PARENT_PATH_PREFIX,
} from '@blaze-runtime/engine-util';
import { type PropertiesConfig } from '@blaze-shared/elements-domain';

import { propsConfigHelper as epc } from '../../utils/elements-props-config.helpers';

import {
  CONTAINER_DEFAULT_HEIGHT,
  CONTAINER_HEIGHT_OPTIONS,
  CONTAINER_MAX_HEIGHT,
} from './_container.models';

export function getIoConfig(): IoConfig {
  return {
    inputs: [
      {
        name: 'visible',
        bindingDefs: [{ property: 'visible', label: 'Is Visible', dataType: 'Boolean' }],
      },
    ],
    outputs: [],
  };
}

export function getContainerDefaults(): PropsOf<'container'> {
  return {
    content: {},
    style: {
      container: {
        offset: { padding: '0px' },
        outlineWidth: '0px',
        fill: { color1: '$t.canvas', selectorType: 'none' },
        imageBehavior: 'cover',
        outline: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
      },
    },
  };
}

export function getContainerSlotDefaults(): PropsOf<'container'> {
  return {
    content: {},
    style: {
      container: {
        offset: { padding: '$t.spacingColumn' },
        outlineWidth: '1px',
        fill: { color1: '$t.canvas', selectorType: 'none' },
        imageBehavior: 'cover',
        outline: { color1: '$t.outline', selectorType: 'none' },
        radius: '$t.radius',
      },
    },
  };
}

//#region Normal Container
export function getContainerPropsConfig(): PropertiesConfig {
  return epc.initConfig('container', [
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.subsection('', 'Minimum Height', [
          epc.root.elementHeightField(
            {
              min: CONTAINER_DEFAULT_HEIGHT,
              max: CONTAINER_MAX_HEIGHT,
              step: 10,
              unit: 'px',
            },
            CONTAINER_HEIGHT_OPTIONS,
            undefined,
            '',
            '0%',
          ),
        ]),
        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowImageFill: true,
          hasOutlineSides: true,
        }),
        epc.subsections.visibilitySubsection(READ_SLOT_PARENT_PATH_PREFIX),
      ],
      true,
    ),
  ]);
}

export function getContainerSlotPropsConfig(): PropertiesConfig {
  return epc.initConfig('container', [
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.columnSizeSubsection('column.sizing'),
        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowImageFill: true,
          hasOutlineSides: true,
        }),
        epc.subsections.visibilitySubsection(READ_EXCLUSIVE_SLOT_PARENT_PATH_PREFIX, {
          visibilityFieldPath: 'content',
        }),
      ],
      true,
    ),
  ]);
}
//#endregion

//#region Navigation Containers
export function getNavigationContainerPropsConfig(
  type: NavigationContainerVariantType,
  opts?: {
    hasFullHeightOption?: boolean;
  },
): PropertiesConfig {
  const pageType = navigationContainerElementTypeToPageType(type);
  const contentOptions = [
    epc.fields.toggleField(`$$page.${pageType}.isUsedToCreateNewPages`, 'Add to new pages', {
      tooltip: 'When checked this element will be added to new pages.',
    }),
    epc.fields.toggleField('content.isSticky', 'Always on screen while scrolling', {
      tooltip: 'This setting works only in live and preview mode.',
    }),
  ];
  if (opts?.hasFullHeightOption) {
    contentOptions.push(
      epc.fields.toggleField('content.isFullHeight', 'Full height', {
        tooltip: 'Expands navigation to cover the full height of the screen.',
      }),
    );
  }

  return epc.initConfig('container', [
    epc.section('content', 'Content', [
      epc.subsections.labelSubsection('Options apply to all project pages'),
      epc.subsections.subsection('content', 'Options', contentOptions),
      epc.subsections.visibilitySubsection(READ_SLOT_PARENT_PATH_PREFIX),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.styleSheetSubsection(),

        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowImageFill: true,
          hasOutlineSides: true,
        }),
      ],
      true,
    ),
  ]);
}

export function getNavigationContainerSlotPropsConfig(
  type: NavigationContainerVariantType,
): PropertiesConfig {
  const pageType = navigationContainerElementTypeToPageType(type);
  return epc.initConfig('container', [
    epc.section('content', 'Content', [
      epc.subsections.labelSubsection('Options apply to all project pages'),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.toggleField(`$$page.${pageType}.isUsedToCreateNewPages`, 'Add to new pages', {
          tooltip: 'When checked this element will be added to new pages.',
        }),
        epc.fields.toggleField(
          `${READ_SLOT_PARENT_PATH_PREFIX}content.isSticky`,
          'Always on screen while scrolling',
          { tooltip: 'This setting works only in live and preview mode.' },
        ),
      ]),
      epc.subsections.visibilitySubsection(READ_SLOT_PARENT_PATH_PREFIX),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),
        epc.subsections.columnSizeSubsection('column.sizing'),

        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowImageFill: true,
          hasOutlineSides: true,
        }),
      ],
      true,
    ),
  ]);
}

export function getSidebarContainerSlotPropsConfig(): PropertiesConfig {
  return epc.initConfig('container', [
    epc.section('content', 'Content', [
      epc.subsections.labelSubsection('Options apply to all project pages'),
      epc.subsections.subsection('content', 'Options', [
        epc.fields.toggleField(`$$page.Sidebar.isUsedToCreateNewPages`, 'Add to new pages', {
          tooltip: 'When checked this element will be added to new pages.',
        }),
        epc.fields.toggleField(
          `${READ_SLOT_PARENT_PATH_PREFIX}content.isSticky`,
          'Always on screen while scrolling',
          { tooltip: 'This setting works only in live and preview mode.' },
        ),
        epc.fields.toggleField(
          `${READ_SLOT_PARENT_PATH_PREFIX}content.isFullHeight`,
          'Full height',
          {
            tooltip: 'Expands navigation to cover the full height of the screen.',
          },
        ),
      ]),
      epc.subsections.visibilitySubsection(READ_SLOT_PARENT_PATH_PREFIX),
    ]),
    epc.section(
      'style',
      'Style',
      [
        epc.subsections.subsection('style.container', 'Padding and Alignment', [
          epc.fields.spacingAndAlignContainerField('style.container.offset', {
            hasPadding: true,
            hasAlign: true,
          }),
        ]),

        epc.subsections.elementBackgroundSubsection('style.container', 'Appearance', {
          allowImageFill: true,
          hasOutlineSides: true,
        }),
      ],
      true,
    ),
  ]);
}
//#endregion
